/**
 * DAL for pending completion notifications
 * Stores and processes notifications to staff when artists complete their profiles or happenings
 */

import { getClient } from './client.js';
import { TABLES } from './tables.js';
import { logger } from './logger.js';
import type {
  PendingCompletionNotification,
  PendingCompletionNotificationCreate,
  CompletionNotificationEntityType,
} from './types.js';

/**
 * Create a pending completion notification
 */
export async function createPendingCompletionNotification(
  input: PendingCompletionNotificationCreate
): Promise<PendingCompletionNotification> {
  const { data, error } = await getClient()
    .from(TABLES.pending_completion_notifications)
    .insert(input)
    .select()
    .single();

  if (error) {
    // If it's a unique constraint violation, the notification already exists
    if (error.code === '23505') {
      logger.info('pending_notification_already_exists', { entityType: input.entity_type, entityId: input.entity_id });
      // Return the existing one
      const existing = await getPendingNotificationByEntity(input.entity_type, input.entity_id);
      if (existing) return existing;
    }
    throw error;
  }

  return data as PendingCompletionNotification;
}

/**
 * Get a pending notification by entity
 */
async function getPendingNotificationByEntity(
  entityType: CompletionNotificationEntityType,
  entityId: string
): Promise<PendingCompletionNotification | null> {
  const { data, error } = await getClient()
    .from(TABLES.pending_completion_notifications)
    .select('*')
    .eq('entity_type', entityType)
    .eq('entity_id', entityId)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  return data as PendingCompletionNotification;
}

/**
 * Get all pending (unsent) completion notifications
 */
export async function getPendingCompletionNotifications(): Promise<PendingCompletionNotification[]> {
  logger.debug('pending_notifications_query_start');

  const { data, error } = await getClient()
    .from(TABLES.pending_completion_notifications)
    .select('*')
    .is('sent_at', null)
    .order('created_at', { ascending: true });

  if (error) {
    logger.error('pending_notifications_query_failed', { error: String(error) });
    throw error;
  }

  logger.info('pending_notifications_query_complete', { found: data?.length || 0 });
  return (data || []) as PendingCompletionNotification[];
}

/**
 * Mark a pending notification as sent
 */
export async function markPendingNotificationSent(id: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.pending_completion_notifications)
    .update({ sent_at: new Date().toISOString() })
    .eq('id', id);

  if (error) throw error;
}

/**
 * Mark a pending notification as failed with an error message
 */
export async function markPendingNotificationFailed(id: string, errorMessage: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.pending_completion_notifications)
    .update({ error_message: errorMessage })
    .eq('id', id);

  if (error) throw error;
}
